import { useMemo } from 'react';
import { useCalendarDataContext } from '../contexts/CalendarDataContext';
import { filterByProjectVisibility, applyColorInheritance } from '../utils/dataUtils';
import { toDateString } from '../utils/dateUtils';
import { SIDEBAR_TYPE, SIDEBAR_MODE } from '../constants/types';

/**
 * Hook to compute visible events and milestones with filtering, preview changes, and color inheritance
 * @param {Object} sidebarState - Sidebar state for applying preview changes
 * @returns {Object} { visibleEvents, visibleMilestones }
 */
export function useVisibleEntities(sidebarState = {}) {
  const { events, milestones, projects } = useCalendarDataContext();
  const { sidebarMode, sidebarType, sidebarData, previewChanges } = sidebarState;
  
  // Compute visible events (filtered by project visibility, with preview changes and color inheritance)
  const visibleEvents = useMemo(() => {
    return filterByProjectVisibility(events, projects, e => e.projectId)
      .map(event => {
        let displayEvent = event;
        
        // Apply preview changes if editing this event
        if (previewChanges && sidebarMode === SIDEBAR_MODE.EDIT && sidebarType === SIDEBAR_TYPE.EVENT && sidebarData?.id === event.id) {
          const changes = { ...previewChanges };
          if (changes.startDate) changes.startDate = toDateString(changes.startDate);
          if (changes.endDate) changes.endDate = toDateString(changes.endDate);
          displayEvent = { ...event, ...changes };
        }
        
        // Apply color inheritance and add read-only and shared project flags
        const eventWithColor = applyColorInheritance(displayEvent, projects);
        const project = projects.find(p => p.id === eventWithColor.projectId);
        return {
          ...eventWithColor,
          isReadOnly: project?.permission === 'read',
          isFromSharedProject: project?.isIncluded === true
        };
      });
  }, [events, projects, previewChanges, sidebarMode, sidebarType, sidebarData]);
  
  // Compute visible milestones (filtered by project visibility, with preview changes and color inheritance)
  const visibleMilestones = useMemo(() => {
    return filterByProjectVisibility(milestones, projects, m => m.projectId)
      .map(milestone => {
        let displayMilestone = milestone;
        
        // Apply preview changes if editing this milestone
        if (previewChanges && sidebarMode === SIDEBAR_MODE.EDIT && sidebarType === SIDEBAR_TYPE.MILESTONE && sidebarData?.id === milestone.id) {
          const changes = { ...previewChanges };
          if (changes.date) changes.date = toDateString(changes.date);
          displayMilestone = { ...milestone, ...changes };
        }
        
        // Apply color inheritance and add read-only and shared project flags
        const milestoneWithColor = applyColorInheritance(displayMilestone, projects);
        const project = projects.find(p => p.id === milestoneWithColor.projectId);
        return {
          ...milestoneWithColor,
          isReadOnly: project?.permission === 'read',
          isFromSharedProject: project?.isIncluded === true
        };
      });
  }, [milestones, projects, previewChanges, sidebarMode, sidebarType, sidebarData]);
  
  return { visibleEvents, visibleMilestones };
}
